import React, { useState, useCallback } from 'react';
import FadeIn from './FadeIn';
import { useImagePreloader } from '../hooks/useImagePreloader';

const galleryImages = [
  { src: "https://ik.imagekit.io/aeu02xvjgn/0281-CA-Padua-HIlls-Theatre-Wedding-Photography.jpg", alt: "Padua Hills Theatre Wedding" },
  { src: "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Melissa-and-Jonathan-52.jpg", alt: "Melissa and Jonathan Wedding" },
  { src: "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Wedding-Photos-Madison-and-Daniel-31.jpg", alt: "Madison and Daniel Wedding" },
  { src: "https://ik.imagekit.io/aeu02xvjgn/i-p39VvTq-XL.jpg", alt: "Padua Hills Wedding Ceremony" },
  { src: "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Kelley-and-Faisal-56.jpg", alt: "Kelley and Faisal Wedding" },
  { src: "https://ik.imagekit.io/aeu02xvjgn/Padua-Hills-Weddings-Angela-and-Mark-141.jpg", alt: "Angela and Mark Wedding" },
];

const Lightbox = ({ image, onClose, onPrev, onNext }) => (
  <div
    className="fixed inset-0 z-50 bg-brand-charcoal/95 flex items-center justify-center p-4"
    onClick={onClose}
  >
    <button
      onClick={onClose}
      className="absolute top-6 right-6 text-brand-vintage/70 hover:text-brand-vintage text-[10px] uppercase tracking-[0.3em] font-bold"
    >
      Close
    </button>
    <button
      onClick={(e) => { e.stopPropagation(); onPrev(); }}
      className="absolute left-4 md:left-6 text-brand-vintage/50 hover:text-brand-vintage text-3xl font-light transition-colors p-2"
    >
      ←
    </button>
    <img
      src={image.src}
      alt={image.alt}
      className="max-h-[85vh] max-w-[85vw] object-contain shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    />
    <button
      onClick={(e) => { e.stopPropagation(); onNext(); }}
      className="absolute right-4 md:right-6 text-brand-vintage/50 hover:text-brand-vintage text-3xl font-light transition-colors p-2"
    >
      →
    </button>
  </div>
);

const Gallery = () => {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  useImagePreloader(galleryImages.map(i => i.src));

  const openLightbox = useCallback((idx) => setLightboxIdx(idx), []);
  const closeLightbox = useCallback(() => setLightboxIdx(null), []);
  const prevImage = useCallback(() => setLightboxIdx(i => (i - 1 + galleryImages.length) % galleryImages.length), []);
  const nextImage = useCallback(() => setLightboxIdx(i => (i + 1) % galleryImages.length), []);

  return (
    <section id="gallery" className="py-24 md:py-32 bg-white px-6">
      <div className="max-w-7xl mx-auto text-center">
        <FadeIn>
          <span className="text-brand-olive uppercase tracking-[0.3em] text-[10px] font-semibold mb-4 block">Visual Stories</span>
          <h2 className="font-serif text-3xl md:text-4xl text-brand-charcoal mb-8 font-light">A Glimpse of the Magic</h2>
          <p className="font-sans text-brand-charcoal/70 mb-16 max-w-xl mx-auto font-light">
            Pictures only tell part of the story. Explore our gallery to see how couples have transformed our historic spaces into their own personal dreams.
          </p>
        </FadeIn>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          {galleryImages.map((img, idx) => (
            <FadeIn key={idx} delay={idx * 0.07}>
              <div
                className="aspect-[4/3] overflow-hidden bg-brand-stone/10 rounded-sm group relative cursor-pointer"
                onClick={() => openLightbox(idx)}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-brand-charcoal/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                  <span className="text-brand-vintage text-[9px] uppercase tracking-[0.3em] font-bold border border-brand-vintage/50 px-4 py-2">View</span>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
        <FadeIn delay={0.3}>
          <a
            href="#contact"
            className="inline-block border border-brand-charcoal text-brand-charcoal px-10 py-4 text-[10px] uppercase tracking-widest hover:bg-brand-charcoal hover:text-white transition-all"
          >
            See the Venue in Person
          </a>
        </FadeIn>
      </div>
      {lightboxIdx !== null && (
        <Lightbox
          image={galleryImages[lightboxIdx]}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}
    </section>
  );
};

export default Gallery;